import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Spinner, Button } from 'react-bootstrap'
import { usePlatformCategoryTree } from './hooks/usePlatformCategoryTree'
import { CategoryNodeData } from './CategoryPickerV2'
import { buildFieldTree, FieldTreeNode } from 'src/utils/buildFieldTree'

type PlatformCategoryTreeSelectProps = {
  platformId: number | undefined
  value: CategoryNodeData | null
  onChange: (val: CategoryNodeData | null) => void
  disabled?: boolean
}

const PlatformCategoryTreeSelect: FC<PlatformCategoryTreeSelectProps> = ({
  platformId,
  value,
  onChange,
  disabled = false,
}) => {
  const {
    results,
    loading,
    fieldLoading,
    hasMore,
    fieldId,
    search,
    loadMore,
    hydrateById,
  } = usePlatformCategoryTree(platformId)

  // ── Internal state ──
  const [isOpen, setIsOpen] = useState(false)
  const [inputTerm, setInputTerm] = useState('')

  const containerRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const hydrationDoneRef = useRef(false)

  // ── Edit mode hydration ──
  // When fieldId resolves and value exists but has no display name,
  // use hydrateById to resolve the name via API 2's selected_child_id.
  useEffect(() => {
    if (hydrationDoneRef.current) return
    if (fieldId == null) return
    if (!value?.id) return
    if (value.name && value.name !== value.id) return

    hydrationDoneRef.current = true
    hydrateById(Number(value.id)).then((name) => {
      if (name) {
        onChange({ ...value, name })
      }
    })
  }, [fieldId, value, hydrateById, onChange])

  // Reset hydration guard when value identity changes
  useEffect(() => {
    hydrationDoneRef.current = false
  }, [value?.id])

  // ── Outside click → close dropdown ──
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setInputTerm('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // ── Input change → call hook's search directly (no extra debounce) ──
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const term = e.target.value
      setInputTerm(term)
      search(term)
    },
    [search]
  )

  // ── Focus → open dropdown, clear inputTerm ──
  const handleInputFocus = useCallback(() => {
    if (disabled || platformId == null) return
    setIsOpen(true)
    setInputTerm('')
    search('')
  }, [disabled, platformId, search])

  // ── Trigger click (closed state) → open dropdown ──
  const handleTriggerClick = useCallback(() => {
    if (disabled || platformId == null) return
    setIsOpen(true)
    setInputTerm('')
  }, [disabled, platformId])

  // ── Item selection ──
  const handleSelect = useCallback(
    (item: any) => {
      onChange({
        id: String(item.id),
        name: item.api_name,
        has_child: false,
        depth: item.depth ?? 0,
        parentId: item.parent_data ? String(item.parent_data) : null,
        raw: item,
      })
      setIsOpen(false)
      setInputTerm('')
    },
    [onChange]
  )

  // ── Clear selection ──
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(null)
      setInputTerm('')
    },
    [onChange]
  )

  // ── Scroll-to-bottom → loadMore ──
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 20) {
        if (hasMore && !loading) {
          loadMore()
        }
      }
    },
    [hasMore, loading, loadMore]
  )

  // ── Tree Data State ──
  const [treeData, setTreeData] = useState<FieldTreeNode[]>([])

  useEffect(() => {
    setTreeData(buildFieldTree(results))
  }, [results])

  const updateNode = (
    nodes: FieldTreeNode[],
    id: number,
    updater: (n: FieldTreeNode) => FieldTreeNode
  ): FieldTreeNode[] =>
    nodes.map((n) =>
      n.id === id
        ? updater(n)
        : { ...n, children: n.children ? updateNode(n.children, id, updater) : n.children }
    )

  // ── Highlight matching term in result text ──
  const renderHighlightedText = (text: string, term?: string) => {
    const trimmedTerm = term?.trim()
    if (!trimmedTerm) return text

    const escapedTerm = trimmedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const parts = text.split(new RegExp(`(${escapedTerm})`, 'ig'))
    const lowerTerm = trimmedTerm.toLowerCase()

    return parts.map((part, index) =>
      part.toLowerCase() === lowerTerm ? (
        <mark key={`${part}-${index}`} className='px-1 py-0 rounded bg-warning bg-opacity-25'>
          {part}
        </mark>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      )
    )
  }

  const renderTreeNodes = (items: FieldTreeNode[], level: number): JSX.Element[] =>
    items.map((node) => {
      const isSelected = node.item && value?.id === String(node.item.id)
      return (
        <div key={node.id} className='mb-1'>
          <div className='d-flex align-items-center'>
            {Array.from({ length: level }).map((_, idx) => (
              <span
                key={idx}
                style={{
                  width: 14,
                  borderLeft: '1px solid #d0d7de',
                  marginRight: 6,
                  height: '100%',
                }}
              ></span>
            ))}
            <Button
              variant='outline-secondary'
              size='sm'
              className='p-0 me-2'
              type='button'
              style={{ width: 22, height: 22, lineHeight: '20px', fontSize: 12 }}
              onClick={(e) => {
                e.stopPropagation()
                setTreeData((prev) =>
                  updateNode(prev, node.id, (n) => ({ ...n, isExpanded: !n.isExpanded }))
                )
              }}
            >
              {node.children && node.children.length
                ? node.isExpanded
                  ? '-'
                  : '+'
                : '-'}
            </Button>
            <span
              role='button'
              className={`flex-grow-1 ${isSelected ? 'fw-semibold text-primary' : ''}`}
              style={{
                padding: '4px 6px',
                borderRadius: 4,
                cursor: 'pointer',
                backgroundColor: isSelected ? '#e7f1ff' : 'transparent',
                fontSize: 13,
              }}
              onClick={() => {
                if (node.isLeaf && node.item) {
                  handleSelect(node.item)
                }
              }}
              onMouseEnter={(e) => {
                if (!isSelected)
                  (e.currentTarget as HTMLSpanElement).style.backgroundColor = '#f6f8fa'
              }}
              onMouseLeave={(e) => {
                if (!isSelected)
                  (e.currentTarget as HTMLSpanElement).style.backgroundColor = 'transparent'
              }}
            >
              {renderHighlightedText(node.api_name, inputTerm)}
            </span>
          </div>
          {node.isExpanded && node.children?.length ? (
            <div style={{ width: '100%' }}>{renderTreeNodes(node.children, level + 1)}</div>
          ) : null}
        </div>
      )
    })

  // ── Determine what the input/trigger displays ──
  const isDisabledState = disabled || platformId == null

  return (
    <div ref={containerRef}>
      <label className='form-label'>Category</label>
      <div className='position-relative'>

        {/* ── Trigger / Input ── */}
        {!isOpen ? (
          /* Closed state — show trigger */
          <div
            className='form-control d-flex align-items-center justify-content-between'
            role='button'
            onClick={handleTriggerClick}
            style={{
              cursor: isDisabledState ? 'not-allowed' : 'pointer',
              opacity: isDisabledState ? 0.65 : 1,
              minHeight: 38,
            }}
          >
            <span className={value?.name ? '' : 'text-muted'}>
              {isDisabledState
                ? 'No platform selected'
                : value?.name || 'Search category...'}
            </span>
            <div className='d-flex align-items-center'>
              {value?.id && !isDisabledState && (
                <span
                  role='button'
                  className='me-2 text-muted'
                  onClick={handleClear}
                  title='Clear category'
                  style={{ fontSize: 16, lineHeight: 1 }}
                >
                  ×
                </span>
              )}
              <i className='bi bi-search me-2 text-muted'></i>
              <i className='bi bi-chevron-down text-muted'></i>
            </div>
          </div>
        ) : (
          /* Open state — show input */
          <div className='position-relative'>
            <input
              className='form-control'
              placeholder='Type to search...'
              value={inputTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              autoFocus
            />
            <i
              className='bi bi-search position-absolute'
              style={{
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6c757d',
              }}
            ></i>
          </div>
        )}

        {/* ── Dropdown ── */}
        {isOpen && (
          <div
            className='position-absolute w-100 bg-white mt-1'
            style={{
              zIndex: 10,
              border: '1px solid #d0d7de',
              borderRadius: 8,
              boxShadow: '0 6px 18px rgba(15,23,42,0.12)',
            }}
          >
            {/* ── Loading field ID ── */}
            {fieldLoading && (
              <div className='d-flex align-items-center gap-2 px-3 py-3 text-muted'>
                <Spinner animation='border' size='sm' /> <span>Loading categories…</span>
              </div>
            )}

            {/* ── Scrollable results list ── */}
            {!fieldLoading && (
              <div
                ref={listRef}
                style={{
                  maxHeight: 320,
                  overflow: 'auto',
                  padding: 8,
                  borderRadius: '0 0 8px 8px',
                }}
                onScroll={handleScroll}
              >


                {/* Searching spinner (only when results are empty and loading) */}
                {inputTerm.trim() && loading && results.length === 0 && (
                  <div className='d-flex align-items-center gap-2 px-2 py-3 text-muted'>
                    <Spinner animation='border' size='sm' /> <span>Searching…</span>
                  </div>
                )}

                {/* No results */}
                {inputTerm.trim() && !loading && results.length === 0 && (
                  <div className='text-muted px-2 py-3'>No results found</div>
                )}

                {/* Result rows */}
                {renderTreeNodes(treeData, 0)}

                {/* Loading more indicator */}
                {loading && results.length > 0 && (
                  <div className='text-center py-2 text-muted small'>Loading more…</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PlatformCategoryTreeSelect
